import { z } from 'zod'

import { ShareScopeSchema } from '~/@entities/Content'
import { ExamAutoAssigningSchema } from '~/@entities/Exam'
import { ServiceTypeSchema } from '~/@entities/Student'
import { WorkbookTypeSchema } from '~/@entities/Workbook'

/***************** 학원 그룹 *****************/
// 여러 학원을 한 브랜드로 묶는 그룹. 소속이 없는 학원은 null 이다.
export const ACADEMY_GROUP_TYPE = ['WEIZMANN', 'CREVERSE', 'VINE'] as const

/***************** 요금제 *****************/
// 학원이 쓰는 기능 범위. 요금제(SUBSCRIBE_PLAN)마다 기본 권한이 정해져 있다.
export const ACADEMY_AUTHORITY = ['BASIC', 'PREMIUM', 'SOLO'] as const

export const SUBSCRIBE_PLAN = [
  'BASIC',
  'BASIC_PLUS',
  'BASIC_CUSTOM',
  'BASIC_UNLIMITED',
  'NEW_BASIC',
  'NEW_BASIC_PLUS',
  'NEW_BASIC_PERSONAL',
  'NEW_BASIC_SPECIAL',
  'NEW_BASIC_CUSTOM',
  'NEW_BASIC_UNLIMITED',
  'PREMIUM',
  'PREMIUM_PLUS',
  'PREMIUM_CUSTOM',
  'PREMIUM_UNLIMITED',
  'PREMIUM_STUDENT_CONTRACT',
  'SOLO',
  'SOLO_PLUS',
  'SOLO_CUSTOM',
] as const

/***************** 가입 경로 *****************/
// 외부 제휴 서비스를 거쳐 가입한 학원만 값이 있고, 직접 가입한 학원은 null 이다.
export const SIGN_UP_FROM = ['NAVER_WORKS', 'WHALE_SPACE', 'B2B_PARTNER_KIMSTUDY', 'AIEP'] as const

/***************** NICE 본인인증 *****************/
// 본인인증을 마친 뒤 서버가 인증 결과로 할 일. 문자 발신번호 등록, 또는 정산 정보 등록 전 본인 정보 확인이다.
export const NICE_AUTH_TYPE = ['UPDATE_PHONE_NUMBER', 'SETTLEMENT_INFO_REGISTER'] as const

export const AcademyGroupTypeSchema = z.enum(ACADEMY_GROUP_TYPE)
export type AcademyGroupType = z.infer<typeof AcademyGroupTypeSchema>

export const AcademyAuthoritySchema = z.enum(ACADEMY_AUTHORITY)
export type AcademyAuthority = z.infer<typeof AcademyAuthoritySchema>

export const SubscribePlanSchema = z.enum(SUBSCRIBE_PLAN)
export type SubscribePlan = z.infer<typeof SubscribePlanSchema>

export const SignUpFromSchema = z.enum(SIGN_UP_FROM)
export type SignUpFrom = z.infer<typeof SignUpFromSchema>

export const NiceAuthTypeSchema = z.enum(NICE_AUTH_TYPE)
export type NiceAuthType = z.infer<typeof NiceAuthTypeSchema>

// SOLO 요금제 학원이 구독한 학교급·학년·교과목. 서버는 외부에서 적힌 JSON 을 그대로 풀어 내므로 세 필드 모두
// null 이 될 수 있고, schoolType 도 enum 이 아닌 문자열이라 좁히지 않는다.
export const SubscribeItemSchema = z.object({
  schoolType: z.string().nullable(),
  grade: z.number().nullable(),
  trieKeys: z.array(z.string()).nullable(),
})
export type SubscribeItem = z.infer<typeof SubscribeItemSchema>

// /academies 응답. 서버는 요금·결제일·주소·메타데이터·공유 설정 등 40여 필드를 내주지만 학생 앱이 읽는 것과
// 트래킹 속성으로 싣는 것만 받는다. 분석 도구에 학원 정보를 싣는 곳도 이 필드만 보낸다.
// customerType 은 enum 이 아니라 한글 분류값(`B2B-학원` 등)이다. customerType·languagePackFileUrl 은 서버 선언이
// non-null 이다(AcademyResponse).
export const AcademySchema = z.object({
  academyId: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  logoUrl: z.string(),
  customerType: z.string(),
  studentCount: z.number(),
  authority: AcademyAuthoritySchema,
  subscribePlan: SubscribePlanSchema.nullable(),
  subscribeItems: z.array(SubscribeItemSchema).nullable(),
  academyGroupType: AcademyGroupTypeSchema.nullable(),
  signUpFrom: SignUpFromSchema.nullable(),
  languagePackFileUrl: z.string(),
  academyAdditionalData: z.object({
    organizationName: z.string().nullable(),
  }),
})
export type Academy = z.infer<typeof AcademySchema>

/** 크레버스 브랜드 판별. 판별 기준이 바뀌면 여기만 수정한다. */
export const isCreverseAcademy = (
  academyGroupType: Academy['academyGroupType'] | undefined,
): boolean => academyGroupType === 'CREVERSE'

/** 바인그룹 브랜드 판별. 판별 기준이 바뀌면 여기만 수정한다. */
export const isVineAcademy = (academyGroupType: Academy['academyGroupType'] | undefined): boolean =>
  academyGroupType === 'VINE'

/***************** 다른 기관 학습지 공유 설정 *****************/
export const WORKSHEET_SHARE_OPTION = ['ALL', 'MY_DB'] as const
export const WorksheetShareOptionSchema = z.enum(WORKSHEET_SHARE_OPTION)
export type WorksheetShareOption = z.infer<typeof WorksheetShareOptionSchema>

const WorksheetShareToggleSchema = z.object({
  enabled: z.boolean(),
  /** 마지막으로 바꾼 시각. 공개를 끈 뒤 1시간 동안은 다시 바꿀 수 없다 */
  datetime: z.iso.datetime({ local: true }),
})

// details 는 생성 타입에서 옵션별 맵이다. 서버는 MY_DB 를 늘 채워 낸다(AcademyWorksheetShareDetailSetting.default·
// getDetail, 2026-10-13 백엔드 develop cc5f60e17 확인). 서버가 함께 내는 myDbWorksheetShared 는 위 두 값에서
// 계산되는 값이라 받지 않는다.
export const AcademyWorksheetShareSettingSchema = z.object({
  allWorksheetShareEnabled: z.boolean(),
  allWorksheetShareDatetime: z.iso.datetime({ local: true }),
  details: z.object({ MY_DB: WorksheetShareToggleSchema }),
})
export type AcademyWorksheetShareSetting = z.infer<typeof AcademyWorksheetShareSettingSchema>

/***************** 학원 상세 *****************/
// 선생님 앱이 /academies 에서 읽는 학원 전체. 학생 앱이 받는 AcademySchema 를 넓혀, 공통 필드는 위 선언 하나를 쓴다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/response/AcademyResponse.kt·AcademyAdditionalDataDto.kt·
// AcademyMetadataKey.kt·service/AcademyReadService.kt 와, 팀이 녹음한 dev 응답 e2e 목(academies.json)으로 확인.

export const ACADEMY_STATUS = [
  'UNKNOWN',
  'FREE_SCHEDULED',
  'FREE_CANCEL',
  'FREE_INPROGRESS',
  'FREE_INPROGRESS_CANCEL',
  'PAY_SCHEDULED',
  'PAY_DENY',
  'PAY_INPROGRESS',
  'PAY_PAUSE',
  'PAY_END',
  'UNPAID_PAUSE',
  'UNPAID_END',
  'DELETED',
] as const
export const AcademyStatusSchema = z.enum(ACADEMY_STATUS)
export type AcademyStatus = z.infer<typeof AcademyStatusSchema>

// 서버가 logoUrl 의 파일 이름으로 정한다. 로고 없음(DELETE)·기본 로고(REPLACE_DEFAULT)·직접 올린 로고(REPLACE)
export const ACADEMY_LOGO_OPERATION = ['DELETE', 'REPLACE', 'REPLACE_DEFAULT'] as const
export const AcademyLogoOperationSchema = z.enum(ACADEMY_LOGO_OPERATION)
export type AcademyLogoOperation = z.infer<typeof AcademyLogoOperationSchema>

// 기본료 위에 붙는 추가 요금의 기준. 푼 문제 수 또는 활동일 수
export const ADDITIONAL_PRICING_PLAN = [
  'NUMBER_OF_SOLVED_PROBLEM',
  'NUMBER_OF_ACTIVE_DAYS',
] as const
export const AdditionalPricingPlanSchema = z.enum(ADDITIONAL_PRICING_PLAN)
export type AdditionalPricingPlan = z.infer<typeof AdditionalPricingPlanSchema>

export const SPECIAL_CONTRACT_TYPE = ['STUDENT_LIMIT_COUNT'] as const

/** PREMIUM_STUDENT_CONTRACT 요금제의 이용권 계약. contractValue 는 계약 종류의 값(학생 수 제한이면 학생 수)이다 */
export const SpecialContractPlanSchema = z.object({
  contractType: z.enum(SPECIAL_CONTRACT_TYPE),
  contractStartDate: z.iso.date(),
  contractValue: z.number().nullable(),
})
export type SpecialContractPlan = z.infer<typeof SpecialContractPlanSchema>

/**
 * 학원 메타데이터. 서버는 키별 기본값을 채워 앞의 열두 키를 늘 싣는다. 학생·선생님 수는 고른 구간 문구이고,
 * 학교급·과목은 쉼표로 저장한 값을 나눈 목록이다. 뒤의 다섯 키는 학원 메타 정보가 있을 때만 실린다.
 */
export const AcademyMetadataSchema = z.object({
  studentCount: z.string(),
  teacherCount: z.string(),
  curriculums: z.array(z.string()),
  subjects: z.array(z.string()),
  smsService: z.boolean(),
  freePremiumAvailable: z.boolean(),
  attendanceSmsService: z.boolean(),
  lectureService: z.boolean(),
  loginServiceByPhoneNumber: z.boolean(),
  thirdPartyProvisionConsentOnPageShop: z.boolean(),
  oneClickWorksheetService: z.boolean(),
  essayAiScoring: z.boolean(),
  popupTargetingGroup: z.string().nullish(),
  /** 매쓰플랫 아카데미 신청일·수료 예정일 */
  academyApplicationDate: z.iso.datetime({ local: true }).nullish(),
  academyExpectedCompletionDate: z.iso.datetime({ local: true }).nullish(),
  academyTrainingBCompleted: z.boolean().optional(),
  academyFinalCompleted: z.boolean().optional(),
})
export type AcademyMetadata = z.infer<typeof AcademyMetadataSchema>

/** 학원 부가 정보. 본인 인증 정보는 문자 발신번호를 인증했을 때만 채워진다 */
export const AcademyAdditionalDataSchema = AcademySchema.shape.academyAdditionalData.extend({
  authName: z.string().nullable(),
  authBirthday: z.string().nullable(),
  authPhoneNumber: z.string().nullable(),
  salesManager: z.string().nullable(),
  sampleSignatureWorkbookShowFlag: z.boolean(),
  contractContactRequestedFlag: z.boolean(),
  organizationDomainName: z.string().nullable(),
  examAutoAssigning: z.array(ExamAutoAssigningSchema),
  isBetaTester: z.boolean(),
  isSeoltab: z.boolean(),
})
export type AcademyAdditionalData = z.infer<typeof AcademyAdditionalDataSchema>

// 날짜는 yyyy-MM-dd 이고 해당 기간이 없으면 null 이다. cellPhone 은 문자 발신번호, repPhone 은 대표 연락처이고
// 미등록이면 빈 문자열이다.
export const AcademyDetailSchema = AcademySchema.extend({
  groupId: z.string().nullable(),
  status: AcademyStatusSchema,
  address: z.string().nullable(),
  addressDetail: z.string().nullable(),
  zipCode: z.string().nullable(),
  cellPhone: z.string(),
  repPhone: z.string(),
  studentLimitCount: z.number().nullable(),
  specialContractPlans: z.array(SpecialContractPlanSchema),
  additionalPricingPlan: AdditionalPricingPlanSchema.nullable(),
  monthlyBaseFee: z.number(),
  /** 매달 결제일(일) */
  payDate: z.number(),
  freeStartDate: z.iso.date().nullable(),
  freeEndDate: z.iso.date().nullable(),
  payStartDate: z.iso.date().nullable(),
  payEndDate: z.iso.date().nullable(),
  freePremiumStartDate: z.iso.date().nullable(),
  freePremiumEndDate: z.iso.date().nullable(),
  onlineOnboardingStartedDate: z.iso.date().nullable(),
  onlineOnboardingEndedDate: z.iso.date().nullable(),
  worksheetShareSetting: AcademyWorksheetShareSettingSchema,
  initialized: z.boolean(),
  initializedDate: z.iso.date().nullable(),
  sellerCode: z.string().nullable(),
  supportedWorkbookTypes: z.array(WorkbookTypeSchema),
  metadata: AcademyMetadataSchema,
  lessonClassCount: z.number(),
  academyAdditionalData: AcademyAdditionalDataSchema,
  availableBetaFeatures: z.array(z.string()),
  serviceSubscriptions: z.array(ServiceTypeSchema),
  logoOperation: AcademyLogoOperationSchema,
  // 학습지·교재 만들기가 공유 범위를 비워 둘 때 쓰는 학원 기본값.
  defaultCustomWorksheetShareScope: ShareScopeSchema,
  defaultCustomWorkbookShareScope: ShareScopeSchema,
})
export type AcademyDetail = z.infer<typeof AcademyDetailSchema>

/***************** B2G 계약 *****************/
// 학교(B2G) 학원이 서명하는 정식 계약. 계약 원본은 인증 서버가 갖고 메인 서버가 옮겨 낸다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/controller/response/B2gContractResponse.kt·
// model/type/B2gContractStatus.kt 로 확인.
export const B2G_CONTRACT_STATUS = ['UNSIGNED', 'PRE_SIGNED', 'SIGNED'] as const
export const B2gContractStatusSchema = z.enum(B2G_CONTRACT_STATUS)
export type B2gContractStatus = z.infer<typeof B2gContractStatusSchema>

// subscribePlan 은 인증 서버가 적은 문자열을 그대로 옮긴 값이라 요금제 enum 으로 좁히지 않는다.
// pdfUrl 은 서명 뒤 계약서 PDF 가 만들어져야 채워진다.
export const B2gContractSchema = z.object({
  academyId: AcademySchema.shape.academyId,
  schoolName: z.string(),
  status: B2gContractStatusSchema,
  amount: z.number(),
  contractStartDateTime: z.iso.datetime({ local: true }),
  contractEndDateTime: z.iso.datetime({ local: true }),
  subscribePlan: z.string(),
  studentCount: z.number(),
  pdfUrl: z.string().nullable(),
})
export type B2gContract = z.infer<typeof B2gContractSchema>

export const B2gContractPdfSchema = z.object({ pdfUrl: z.string() })
export type B2gContractPdf = z.infer<typeof B2gContractPdfSchema>

/***************** NICE 본인인증 *****************/
// 본인인증 창(NICE)을 열 때 폼에 싣는 암호문. 앱은 해석하지 않고 그대로 보낸다
export const NiceEncodeDataSchema = z.string()
export type NiceEncodeData = z.infer<typeof NiceEncodeDataSchema>

/***************** NICE 본인인증 결과 *****************/
// 정산 정보 등록 전 본인인증으로 서버가 복호화한 본인 정보(NiceDecodedResponse). 세 값 모두 NICE 가 준 문자열 그대로다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 academy/service/NiceService.kt·controller/response/NiceDecodedResponse.kt 로 확인.
export const NiceIdentitySchema = z.object({
  academyId: AcademySchema.shape.academyId,
  name: z.string(),
  phoneNumber: z.string(),
  birthDate: z.string(),
})
export type NiceIdentity = z.infer<typeof NiceIdentitySchema>
